import React, { useState, useRef } from 'react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { 
  ArrowLeft, 
  Bot, 
  Upload, 
  X, 
  CheckCircle2, 
  AlertCircle, 
  Loader2,
  Copy,
  Download,
  FileJson,
  DollarSign
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { uploadFile } from "@/integrations/Core";

// Função para converter valores numéricos em formato brasileiro para float
const convertToFloat = (value) => {
  if (value === null || value === undefined) return null;
  if (typeof value === 'number') return value;
  
  // Se for string, tentar converter
  if (typeof value === 'string') {
    // Log detalhado da conversão
    console.log(`Convertendo valor: "${value}" (${typeof value})`);
    
    // Remover pontos de separação de milhar e substituir vírgula por ponto
    const cleanValue = value.replace(/\./g, '').replace(',', '.');
    console.log(`Valor limpo: "${cleanValue}"`);
    
    const floatValue = parseFloat(cleanValue);
    console.log(`Valor convertido: ${floatValue} (${typeof floatValue})`);
    
    if (isNaN(floatValue)) {
      console.warn(`Não foi possível converter "${value}" para número`);
      return null;
    }
    
    return floatValue;
  }
  
  return null;
};

// Função para preparar os dados para o agente
const prepareDataForAgent = (data) => {
  // Clonar os dados para não modificar o original
  const clonedData = JSON.parse(JSON.stringify(data));
  
  // Log do início da preparação dos dados
  console.log('Iniciando preparação dos dados para o agente...');
  
  // Processar cada item em IRPFBens
  if (clonedData.IRPFBens && Array.isArray(clonedData.IRPFBens)) {
    clonedData.IRPFBens.forEach(item => {
      if (item.Tasks) {
        // Processar empresas
        if (item.Tasks.Empresa && Array.isArray(item.Tasks.Empresa)) {
          console.log(`Processando ${item.Tasks.Empresa.length} empresas...`);
          item.Tasks.Empresa.forEach((empresa, index) => {
            const original2023 = empresa.situacao_2023;
            const original2024 = empresa.situacao_2024;
            
            empresa.situacao_2023 = convertToFloat(empresa.situacao_2023);
            empresa.situacao_2024 = convertToFloat(empresa.situacao_2024);
            
            console.log(`Empresa ${index + 1} (${empresa.cnpj}): 
              2023: ${original2023} -> ${empresa.situacao_2023}
              2024: ${original2024} -> ${empresa.situacao_2024}`);
          });
        }
        
        // Processar veículos
        if (item.Tasks.Veiculo && Array.isArray(item.Tasks.Veiculo)) {
          console.log(`Processando ${item.Tasks.Veiculo.length} veículos...`);
          item.Tasks.Veiculo.forEach((veiculo, index) => {
            const original2023 = veiculo.situacao_2023;
            const original2024 = veiculo.situacao_2024;
            
            veiculo.situacao_2023 = convertToFloat(veiculo.situacao_2023);
            veiculo.situacao_2024 = convertToFloat(veiculo.situacao_2024);
            
            console.log(`Veículo ${index + 1} (${veiculo.renavam}): 
              2023: ${original2023} -> ${veiculo.situacao_2023}
              2024: ${original2024} -> ${veiculo.situacao_2024}`);
          });
        }
      }
    });
  }
  
  // Log da preparação concluída
  console.log('Preparação dos dados concluída.');
  
  return clonedData;
};

export default function AgentIRPF() {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [addFirstPhaseRevenue, setAddFirstPhaseRevenue] = useState(false);

  // Função para salvar log
  const saveLog = async (action, data = {}, response = null, error = null) => {
    try {
      const timestamp = new Date().toISOString();
      const logEntry = {
        timestamp,
        action,
        data,
        response,
        error: error ? error.toString() : null
      };
      
      // Enviar log para o servidor
      const logData = {
        type: "DEBUG",
        message: `${action}: ${JSON.stringify(data).substring(0, 100)}...`,
        data: {
          action,
          details: data,
          response: response ? response : null,
          error: error ? error.toString() : null,
          timestamp
        }
      };
      
      console.log("Enviando log para servidor:", logData);
      
      // Fazer requisição para o endpoint de log
      try {
        const logResponse = await fetch('/api/log.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(logData)
        });
        
        if (!logResponse.ok) {
          console.error('Erro ao salvar log no servidor:', await logResponse.text());
        } else {
          console.log(`Log salvo com sucesso: ${action}`);
        }
      } catch (serverLogError) {
        console.error('Exceção ao tentar salvar log no servidor:', serverLogError);
      }
    } catch (logError) {
      console.error('Erro ao preparar log:', logError);
    }
  };

  // Função para salvar log detalhado em arquivo específico
  const saveDetailedLog = async (action, data, response = null, error = null) => {
    try {
      const today = new Date();
      const dateStr = today.toISOString().split('T')[0]; // formato YYYY-MM-DD
      const timestamp = today.toISOString();
      
      const logEntry = {
        timestamp,
        action,
        data,
        response,
        error: error ? error.toString() : null
      };
      
      // Criar o nome do arquivo de log com a data atual
      const fileName = `Log-AgentIRPF-${dateStr}.json`;
      
      // Preparar o conteúdo do log
      const logContent = JSON.stringify(logEntry, null, 2);
      
      // Criar um blob com o conteúdo do log
      const blob = new Blob([logContent], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', blob, fileName);
      formData.append('append', 'true'); // Indicar que queremos anexar ao arquivo existente
      
      // Enviar o log para o servidor
      const saveResponse = await fetch('/api/save_log.php', {
        method: 'POST',
        body: formData
      });
      
      if (!saveResponse.ok) {
        console.error(`Erro ao salvar log detalhado: ${saveResponse.statusText}`);
      } else {
        console.log(`Log detalhado salvo com sucesso: ${fileName}`);
      }
    } catch (logError) {
      console.error('Erro ao salvar log detalhado:', logError);
    }
  };

  // Função para lidar com eventos de arrastar arquivos
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  // Função para lidar com soltar arquivos
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const droppedFiles = Array.from(e.dataTransfer.files);
    setFiles((prev) => [...prev, ...droppedFiles]);
  };

  // Função para lidar com seleção de arquivos pelo input
  const handleFileInput = (e) => {
    const selectedFiles = Array.from(e.target.files);
    setFiles((prev) => [...prev, ...selectedFiles]);
  };

  // Função para abrir o seletor de arquivos
  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  // Função para remover um arquivo
  const removeFile = (indexToRemove) => {
    setFiles((prev) => prev.filter((_, index) => index !== indexToRemove));
  };

  // Função para formatar o tamanho do arquivo
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(2) + ' KB';
    else return (bytes / 1048576).toFixed(2) + ' MB';
  };

  // Função para processar os arquivos JSON e enviar para o agente
  const processFiles = async () => {
    if (files.length === 0) {
      setError("Selecione pelo menos um arquivo JSON para processar");
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      // Registrar início do processamento
      await saveLog('INICIO_PROCESSAMENTO', { 
        filesCount: files.length, 
        addFirstPhaseRevenue 
      });
      
      // Log detalhado do início do processamento
      await saveDetailedLog('INICIO_PROCESSAMENTO', { 
        filesCount: files.length, 
        fileNames: files.map(f => f.name),
        addFirstPhaseRevenue 
      });
      
      // Verificar se os arquivos necessários já existem no servidor
      let empresasData = null;
      let veiculosData = null;
      
      try {
        // Verificar se os arquivos foram enviados pelo usuário
        const empresasFile = files.find(file => file.name.toLowerCase().includes('empresa'));
        const veiculosFile = files.find(file => file.name.toLowerCase().includes('veiculo'));
        
        // Log dos arquivos encontrados
        await saveLog('ARQUIVOS_ENCONTRADOS', { 
          empresasFile: empresasFile ? empresasFile.name : null,
          veiculosFile: veiculosFile ? veiculosFile.name : null
        });
        
        // Se não encontrou arquivos específicos, tentar carregar do servidor
        if (!empresasFile && !veiculosFile) {
          // Tentar carregar o arquivo Empresas.json do servidor
          const empresasResponse = await fetch('/files/Empresas.json');
          if (empresasResponse.ok) {
            const empresasText = await empresasResponse.text();
            empresasData = JSON.parse(empresasText);
            console.log("Arquivo Empresas.json carregado do servidor com sucesso");
            await saveLog('ARQUIVO_CARREGADO_SERVIDOR', { arquivo: 'Empresas.json' });
            await saveDetailedLog('ARQUIVO_CARREGADO_SERVIDOR', { 
              arquivo: 'Empresas.json',
              tamanho: empresasText.length
            });
          } else {
            console.warn("Arquivo Empresas.json não encontrado no servidor, buscando nos arquivos enviados");
            await saveLog('ARQUIVO_NAO_ENCONTRADO_SERVIDOR', { arquivo: 'Empresas.json' });
          }
          
          // Tentar carregar o arquivo Veiculos.json do servidor
          const veiculosResponse = await fetch('/files/Veiculos.json');
          if (veiculosResponse.ok) {
            const veiculosText = await veiculosResponse.text();
            veiculosData = JSON.parse(veiculosText);
            console.log("Arquivo Veiculos.json carregado do servidor com sucesso");
            await saveLog('ARQUIVO_CARREGADO_SERVIDOR', { arquivo: 'Veiculos.json' });
            await saveDetailedLog('ARQUIVO_CARREGADO_SERVIDOR', { 
              arquivo: 'Veiculos.json',
              tamanho: veiculosText.length
            });
          } else {
            console.warn("Arquivo Veiculos.json não encontrado no servidor, buscando nos arquivos enviados");
            await saveLog('ARQUIVO_NAO_ENCONTRADO_SERVIDOR', { arquivo: 'Veiculos.json' });
          }
        }
      } catch (error) {
        console.warn("Erro ao carregar arquivos do servidor:", error);
        await saveLog('AVISO_CARREGAR_ARQUIVOS_SERVIDOR', {}, null, error);
        await saveDetailedLog('ERRO_CARREGAR_ARQUIVOS_SERVIDOR', {}, null, error);
      }
      
      // Se não conseguiu carregar do servidor, fazer upload e processar os arquivos enviados
      if (!empresasData || !veiculosData) {
        // Primeiro, fazer upload dos arquivos
        const uploadedFiles = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setUploadProgress((prev) => ({ ...prev, [i]: 0 }));

          try {
            // Log do início do upload
            await saveLog('INICIO_UPLOAD', { fileName: file.name, fileSize: file.size });
            
            // Upload do arquivo
            const result = await uploadFile(file, (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [i]: progress,
              }));
            });
            
            // Log do sucesso do upload
            await saveLog('UPLOAD_SUCESSO', { fileName: file.name, result });
            
            uploadedFiles.push({
              name: file.name,
              url: result.url,
              size: file.size,
              file: file // Armazenar referência ao arquivo original
            });
          } catch (error) {
            // Log do erro de upload
            await saveLog('ERRO_UPLOAD', { fileName: file.name }, null, error);
            console.error(`Erro ao fazer upload do arquivo ${file.name}:`, error);
            setError(`Erro ao fazer upload do arquivo ${file.name}: ${error.message}`);
            setProcessing(false);
            return;
          }
        }
        
        // Processar os arquivos enviados
        for (const uploadedFile of uploadedFiles) {
          try {
            // Obter o conteúdo do arquivo
            let fileContent;
            
            // Primeiro tentar ler o arquivo diretamente
            if (uploadedFile.file) {
              const reader = new FileReader();
              
              // Criar uma Promise para aguardar a leitura do arquivo
              const readFilePromise = new Promise((resolve, reject) => {
                reader.onload = (event) => resolve(event.target.result);
                reader.onerror = (error) => reject(error);
              });
              
              // Iniciar a leitura do arquivo
              reader.readAsText(uploadedFile.file);
              
              // Aguardar a leitura do arquivo
              const fileText = await readFilePromise;
              
              // Tentar fazer o parse do JSON
              try {
                fileContent = JSON.parse(fileText);
                await saveLog('ARQUIVO_LIDO_DIRETAMENTE', { 
                  fileName: uploadedFile.name,
                  tamanho: fileText.length
                });
              } catch (parseError) {
                await saveLog('ERRO_PARSE_JSON_LOCAL', { fileName: uploadedFile.name }, null, parseError);
                throw new Error(`Erro ao analisar o JSON do arquivo ${uploadedFile.name}: ${parseError.message}`);
              }
            } 
            
            // Se não conseguiu ler diretamente, tentar pela URL
            if (!fileContent) {
              const response = await fetch(uploadedFile.url);
              if (!response.ok) {
                throw new Error(`Erro ao ler o arquivo ${uploadedFile.name}: ${response.statusText}`);
              }
              
              // Obter o texto da resposta primeiro para verificar
              const text = await response.text();
              
              // Verificar se o texto começa com <!DOCTYPE ou <html, o que indicaria HTML em vez de JSON
              if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().toLowerCase().startsWith('<html')) {
                throw new Error(`O arquivo ${uploadedFile.name} retornou HTML em vez de JSON. Verifique se o arquivo existe e está acessível.`);
              }
              
              // Tentar fazer o parse do JSON
              try {
                fileContent = JSON.parse(text);
              } catch (parseError) {
                throw new Error(`Erro ao analisar o JSON do arquivo ${uploadedFile.name}: ${parseError.message}`);
              }
            }
            
            // Verificar se o arquivo tem a estrutura esperada
            if (!fileContent.IRPFBens || !fileContent.IRPFBens[0] || !fileContent.IRPFBens[0].Tasks) {
              throw new Error(`Arquivo ${uploadedFile.name} não tem a estrutura esperada para IRPF`);
            }
            
            // Log do conteúdo do arquivo
            await saveLog('CONTEUDO_ARQUIVO', { 
              fileName: uploadedFile.name,
              preview: JSON.stringify(fileContent).substring(0, 200) + '...'
            });
            
            // Armazenar os dados conforme o nome do arquivo
            if (uploadedFile.name.toLowerCase().includes('empresa')) {
              empresasData = fileContent;
              await saveLog('DADOS_EMPRESA_CARREGADOS', { 
                fileName: uploadedFile.name,
                tamanho: JSON.stringify(fileContent).length
              });
            } else if (uploadedFile.name.toLowerCase().includes('veiculo')) {
              veiculosData = fileContent;
              await saveLog('DADOS_VEICULO_CARREGADOS', { 
                fileName: uploadedFile.name,
                tamanho: JSON.stringify(fileContent).length
              });
            } else {
              // Se o nome do arquivo não contém empresa ou veículo
              // Verificar o conteúdo para determinar o tipo
              if (fileContent.IRPFBens[0].Tasks.Empresa) {
                empresasData = fileContent;
                await saveLog('DADOS_EMPRESA_INFERIDOS', { 
                  fileName: uploadedFile.name,
                  tamanho: JSON.stringify(fileContent).length
                });
              } else if (fileContent.IRPFBens[0].Tasks.Veiculo) {
                veiculosData = fileContent;
                await saveLog('DADOS_VEICULO_INFERIDOS', { 
                  fileName: uploadedFile.name,
                  tamanho: JSON.stringify(fileContent).length
                });
              }
            }
            
          } catch (error) {
            await saveLog('ERRO_PROCESSAMENTO_ARQUIVO', { fileName: uploadedFile.name }, null, error);
            console.error(`Erro ao processar o arquivo ${uploadedFile.name}:`, error);
            setError(`Erro ao processar o arquivo ${uploadedFile.name}: ${error.message}`);
            setProcessing(false);
            return;
          }
        }
      }
      
      // Verificar se temos os dados necessários
      if (!empresasData && !veiculosData) {
        throw new Error("Não foi possível obter os dados de Empresas e Veículos. Verifique se os arquivos foram enviados ou existem no servidor.");
      }
      
      // Estrutura para armazenar os dados processados
      const processedData = {
        IRPFBens: [
          {
            Tasks: {}
          }
        ]
      };
      
      // Processar os dados de Empresas
      if (empresasData && empresasData.IRPFBens && empresasData.IRPFBens[0] && empresasData.IRPFBens[0].Tasks) {
        const empresasTasks = empresasData.IRPFBens[0].Tasks;
        
        // Adicionar cada tipo de tarefa ao resultado processado
        Object.keys(empresasTasks).forEach(taskType => {
          if (!processedData.IRPFBens[0].Tasks[taskType]) {
            processedData.IRPFBens[0].Tasks[taskType] = [];
          }
          
          // Adicionar os itens ao array correspondente
          processedData.IRPFBens[0].Tasks[taskType] = [
            ...processedData.IRPFBens[0].Tasks[taskType],
            ...empresasTasks[taskType]
          ];
        });
      }
      
      // Processar os dados de Veículos
      if (veiculosData && veiculosData.IRPFBens && veiculosData.IRPFBens[0] && veiculosData.IRPFBens[0].Tasks) {
        const veiculosTasks = veiculosData.IRPFBens[0].Tasks;
        
        // Adicionar cada tipo de tarefa ao resultado processado
        Object.keys(veiculosTasks).forEach(taskType => {
          if (!processedData.IRPFBens[0].Tasks[taskType]) {
            processedData.IRPFBens[0].Tasks[taskType] = [];
          }
          
          // Adicionar os itens ao array correspondente
          processedData.IRPFBens[0].Tasks[taskType] = [
            ...processedData.IRPFBens[0].Tasks[taskType],
            ...veiculosTasks[taskType]
          ];
        });
      }
      
      // Adicionar rendimentos se a opção estiver ativada
      if (addFirstPhaseRevenue) {
        // Gerar valores aleatórios para os rendimentos entre 34400 e 35400
        const totalTarget = Math.random() * (35400 - 34400) + 34400; // Valor total entre 34400 e 35400
        const avgMonthly = totalTarget / 12; // Média mensal
        
        // Gerar valores para 11 meses (o último será calculado para garantir o total exato)
        const monthlyValues = [];
        let currentTotal = 0;
        
        // Gerar valores para os primeiros 11 meses
        for (let i = 0; i < 11; i++) {
          // Variação de até 5% para mais ou para menos em relação à média
          const variation = (Math.random() * 0.1 - 0.05) * avgMonthly;
          const monthValue = avgMonthly + variation;
          monthlyValues.push(parseFloat(monthValue.toFixed(2)));
          currentTotal += monthlyValues[i];
        }
        
        // Calcular o valor do último mês para atingir o total exato
        const lastMonthValue = parseFloat((totalTarget - currentTotal).toFixed(2));
        monthlyValues.push(lastMonthValue);
        
        // Criar o objeto de rendimentos
        const rendimentos = {
          janeiro: monthlyValues[0],
          fevereiro: monthlyValues[1],
          marco: monthlyValues[2],
          abril: monthlyValues[3],
          maio: monthlyValues[4],
          junho: monthlyValues[5],
          julho: monthlyValues[6],
          agosto: monthlyValues[7],
          setembro: monthlyValues[8],
          outubro: monthlyValues[9],
          novembro: monthlyValues[10],
          dezembro: monthlyValues[11]
        };
        
        // Adicionar ao resultado processado
        processedData.IRPFBens[0].Tasks.Rendimento = [rendimentos];
        
        // Log dos rendimentos gerados
        await saveLog('RENDIMENTOS_GERADOS', { 
          totalTarget, 
          rendimentos 
        });
        
        await saveDetailedLog('RENDIMENTOS_GERADOS', { 
          totalTarget, 
          avgMonthly,
          rendimentos 
        });
      }
      
      // Converter valores numéricos antes de enviar ao agente
      const dataForAgent = prepareDataForAgent(processedData);
      
      // Log dos dados preparados para o agente
      await saveLog('DADOS_PREPARADOS_PARA_AGENTE', { 
        dadosOriginais: {
          amostra: JSON.stringify(processedData).substring(0, 100) + '...'
        },
        dadosConvertidos: {
          amostra: JSON.stringify(dataForAgent).substring(0, 100) + '...'
        }
      });
      
      // Log do resultado do processamento
      await saveLog('PROCESSAMENTO_CONCLUIDO', { dataForAgent });
      
      // Log detalhado do resultado do processamento
      await saveDetailedLog('PROCESSAMENTO_CONCLUIDO', { 
        dataForAgent,
        timestamp: new Date().toISOString(),
        addFirstPhaseRevenue,
        filesProcessed: files.map(f => f.name)
      });
      
      // Enviar os dados para o agente
      try {
        await saveLog('ENVIANDO_PARA_AGENTE', { url: 'http://host-lenovo.ddns.net:8000/IRPF/Bens' });
        
        // Salvar os dados processados em um arquivo de log para referência
        await saveDetailedLog('DADOS_ENVIADOS_PARA_AGENTE', { 
          dataForAgent: JSON.parse(JSON.stringify(dataForAgent)),
          timestamp: new Date().toISOString()
        });
        
        // Log detalhado dos dados enviados para o agente
        await saveDetailedLog('ENVIANDO_PARA_AGENTE', { 
          url: 'http://host-lenovo.ddns.net:8000/IRPF/Bens',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer fastapi_live_f6d0d199fa86abaa4d1169983c5f3f66'
          },
          data: dataForAgent,
          timestamp: new Date().toISOString()
        });
        
        // Atualizar mensagem de processamento
        setProcessing(true);
        setError(null);
        setResult({
          message: "Enviando dados para o agente...",
          status: "sending",
          timestamp: new Date().toISOString()
        });
        
        // Salvar os dados processados em um arquivo JSON para referência
        try {
          const agentDataBlob = new Blob([JSON.stringify(dataForAgent, null, 2)], { type: 'application/json' });
          const formData = new FormData();
          formData.append('file', agentDataBlob, 'AgentIRPF.json');
          
          const saveResponse = await fetch('/api/save_log.php', {
            method: 'POST',
            body: formData
          });
          
          if (saveResponse.ok) {
            console.log('Dados do agente salvos em arquivo para referência');
            await saveLog('DADOS_AGENTE_SALVOS', { 
              arquivo: 'AgentIRPF.json',
              tamanho: JSON.stringify(dataForAgent).length
            });
          }
        } catch (saveError) {
          console.warn('Erro ao salvar dados do agente em arquivo:', saveError);
        }
        
        // Log de debug antes de enviar para o proxy
        console.log('DEBUG - Enviando dados para o proxy:', {
          url: '/api/agent_proxy.php',
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          dataSize: JSON.stringify(dataForAgent).length,
          dataPreview: JSON.stringify(dataForAgent).substring(0, 500) + '...'
        });
        
        await saveLog('ENVIANDO_PARA_PROXY', { 
          url: '/api/agent_proxy.php',
          dataSize: JSON.stringify(dataForAgent).length,
          dataPreview: JSON.stringify(dataForAgent).substring(0, 200) + '...'
        });
        
        // Usar o proxy PHP que utiliza cURL para fazer a requisição
        const agentResponse = await fetch('/api/agent_proxy.php', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(dataForAgent)
        });
        
        // Log de debug após receber resposta do proxy
        console.log('DEBUG - Resposta do proxy:', {
          status: agentResponse.status,
          statusText: agentResponse.statusText,
          headers: Object.fromEntries([...agentResponse.headers.entries()])
        });
        
        await saveLog('RESPOSTA_DO_PROXY', { 
          status: agentResponse.status,
          statusText: agentResponse.statusText,
          headers: Object.fromEntries([...agentResponse.headers.entries()])
        });
        
        // Obter o texto da resposta para log
        const responseText = await agentResponse.text();
        console.log('DEBUG - Texto da resposta:', responseText.substring(0, 500));
        
        await saveLog('TEXTO_RESPOSTA_PROXY', { 
          responsePreview: responseText.substring(0, 500)
        });
        
        let agentResult;
        
        try {
          // Tentar fazer parse do JSON da resposta
          agentResult = JSON.parse(responseText);
          console.log('DEBUG - JSON da resposta:', agentResult);
          
          await saveLog('JSON_RESPOSTA_PROXY', { 
            parsedJson: agentResult
          });
        } catch (e) {
          // Se não for um JSON válido, usar o texto como está
          agentResult = { response: responseText };
          console.error('DEBUG - Erro ao fazer parse do JSON:', e);
          
          await saveLog('ERRO_PARSE_JSON', { 
            error: e.toString(),
            responseText: responseText.substring(0, 500)
          });
        }
        
        if (!agentResponse.ok) {
          console.error('DEBUG - Resposta não ok:', {
            status: agentResponse.status,
            statusText: agentResponse.statusText,
            responseText: responseText.substring(0, 500)
          });
          
          await saveLog('RESPOSTA_NAO_OK', { 
            status: agentResponse.status,
            statusText: agentResponse.statusText,
            responseText: responseText.substring(0, 500)
          });
          
          throw new Error(`Erro ao enviar dados para o agente: ${agentResponse.status} - ${responseText}`);
        }
        
        // Log da resposta do agente
        await saveLog('RESPOSTA_AGENTE_SUCESSO', { 
          status: agentResponse.status,
          agentResult 
        });
        
        // Log detalhado da resposta do agente
        await saveDetailedLog('RESPOSTA_AGENTE_SUCESSO', { 
          status: agentResponse.status,
          statusText: agentResponse.statusText,
          headers: Object.fromEntries([...agentResponse.headers.entries()]),
          agentResult,
          timestamp: new Date().toISOString()
        });
        
        // Salvar o resultado em um arquivo local também
        try {
          const blob = new Blob([JSON.stringify(dataForAgent, null, 2)], { type: 'application/json' });
          const formData = new FormData();
          formData.append('file', blob, 'AgentIRPF.json');
          
          const saveResponse = await fetch('/api/save_file.php', {
            method: 'POST',
            body: formData
          });
          
          if (!saveResponse.ok) {
            console.warn(`Aviso: Não foi possível salvar o arquivo localmente: ${saveResponse.statusText}`);
            await saveLog('AVISO_SALVAR_ARQUIVO', { statusText: saveResponse.statusText });
          } else {
            const saveResult = await saveResponse.json();
            await saveLog('ARQUIVO_SALVO', { saveResult });
          }
        } catch (saveError) {
          console.warn("Aviso: Erro ao salvar o arquivo localmente:", saveError);
          await saveLog('ERRO_SALVAR_ARQUIVO', {}, null, saveError);
        }
        
        // Se a resposta for com código 200, mostrar apenas "Task Started"
        if (agentResponse.status === 200) {
          setResult({
            message: "Task Started",
            status: "success",
            timestamp: new Date().toISOString(),
            processedData: dataForAgent
          });
        } else {
          setResult({
            message: "Dados enviados com sucesso para o agente!",
            status: "success",
            timestamp: new Date().toISOString(),
            agentResponse: agentResult,
            processedData: dataForAgent
          });
        }
        
      } catch (agentError) {
        await saveLog('ERRO_ENVIO_AGENTE', {}, null, agentError);
        await saveDetailedLog('ERRO_ENVIO_AGENTE', {
          error: agentError.toString(),
          stack: agentError.stack,
          timestamp: new Date().toISOString()
        });
        console.error("Erro ao enviar dados para o agente:", agentError);
        setError(`Erro ao enviar dados para o agente: ${agentError.message}`);
      }
      
    } catch (error) {
      // Log do erro geral
      await saveLog('ERRO_PROCESSAMENTO', {}, null, error);
      await saveDetailedLog('ERRO_PROCESSAMENTO', {
        error: error.toString(),
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
      console.error("Erro ao processar arquivos:", error);
      setError(`Erro ao processar arquivos: ${error.message}`);
    } finally {
      setProcessing(false);
    }
  };

  // Função para copiar o JSON para a área de transferência
  const copyToClipboard = () => {
    if (result) {
      navigator.clipboard.writeText(JSON.stringify(result, null, 2))
        .then(() => {
          alert("JSON copiado para a área de transferência!");
        })
        .catch(err => {
          console.error("Erro ao copiar para a área de transferência:", err);
          alert("Erro ao copiar para a área de transferência");
        });
    }
  };

  // Função para baixar o JSON
  const downloadJson = () => {
    if (result) {
      const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(result, null, 2));
      const downloadAnchorNode = document.createElement('a');
      downloadAnchorNode.setAttribute("href", dataStr);
      downloadAnchorNode.setAttribute("download", "AgentIRPF.json");
      document.body.appendChild(downloadAnchorNode);
      downloadAnchorNode.click();
      downloadAnchorNode.remove();
    }
  };

  // Função para iniciar o processamento com o agente
  const startAgent = async () => {
    if (files.length === 0) {
      setError("Selecione pelo menos um arquivo JSON para processar");
      return;
    }

    setProcessing(true);
    setError(null);
    setResult(null);

    try {
      // Verificar se temos apenas um arquivo ou múltiplos
      if (files.length === 1) {
        // Processar um único arquivo diretamente
        const singleFile = files[0];
        
        // Ler o conteúdo do arquivo
        const reader = new FileReader();
        
        // Criar uma Promise para aguardar a leitura do arquivo
        const readFilePromise = new Promise((resolve, reject) => {
          reader.onload = (event) => resolve(event.target.result);
          reader.onerror = (error) => reject(error);
        });
        
        // Iniciar a leitura do arquivo
        reader.readAsText(singleFile);
        
        // Aguardar a leitura do arquivo
        const fileText = await readFilePromise;
        
        // Tentar fazer o parse do JSON
        let fileContent;
        try {
          fileContent = JSON.parse(fileText);
          await saveLog('ARQUIVO_LIDO_DIRETAMENTE', { 
            fileName: singleFile.name,
            tamanho: fileText.length
          });
        } catch (parseError) {
          await saveLog('ERRO_PARSE_JSON_LOCAL', { fileName: singleFile.name }, null, parseError);
          throw new Error(`Erro ao analisar o JSON do arquivo ${singleFile.name}: ${parseError.message}`);
        }
        
        // Verificar se o arquivo tem a estrutura esperada
        if (!fileContent.IRPFBens || !fileContent.IRPFBens[0] || !fileContent.IRPFBens[0].Tasks) {
          throw new Error(`Arquivo ${singleFile.name} não tem a estrutura esperada para IRPF`);
        }
        
        // Preparar dados para o agente
        const dataForAgent = prepareDataForAgent(fileContent);
        
        // Log dos dados preparados para o agente
        await saveLog('DADOS_PREPARADOS_PARA_AGENTE', { 
          dadosOriginais: {
            amostra: JSON.stringify(fileContent).substring(0, 100) + '...'
          },
          dadosConvertidos: {
            amostra: JSON.stringify(dataForAgent).substring(0, 100) + '...'
          }
        });
        
        // Enviar para o agente
        await enviarParaAgente(dataForAgent);
      } else {
        // Processar múltiplos arquivos (usar a lógica do processFiles)
        await processarMultiplosArquivos();
      }
    } catch (error) {
      console.error('Erro ao processar com o agente:', error);
      setError(`Erro ao processar com o agente: ${error.message}`);
      setProcessing(false);
      
      await saveLog('ERRO_PROCESSAR_AGENTE', {}, null, error);
      await saveDetailedLog('ERRO_PROCESSAR_AGENTE', {
        error: error.toString(),
        stack: error.stack,
        timestamp: new Date().toISOString()
      });
    } finally {
      setProcessing(false);
    }
  };
  
  // Função para processar múltiplos arquivos (extraída da lógica do processFiles)
  const processarMultiplosArquivos = async () => {
    try {
      // Registrar início do processamento
      await saveLog('INICIO_PROCESSAMENTO_MULTIPLOS', { 
        filesCount: files.length, 
        addFirstPhaseRevenue 
      });
      
      // Verificar se os arquivos necessários já existem no servidor
      let empresasData = null;
      let veiculosData = null;
      
      try {
        // Verificar se os arquivos foram enviados pelo usuário
        const empresasFile = files.find(file => file.name.toLowerCase().includes('empresa'));
        const veiculosFile = files.find(file => file.name.toLowerCase().includes('veiculo'));
        
        // Log dos arquivos encontrados
        await saveLog('ARQUIVOS_ENCONTRADOS', { 
          empresasFile: empresasFile ? empresasFile.name : null,
          veiculosFile: veiculosFile ? veiculosFile.name : null
        });
        
        // Se não encontrou arquivos específicos, tentar carregar do servidor
        if (!empresasFile && !veiculosFile) {
          // Tentar carregar o arquivo Empresas.json do servidor
          const empresasResponse = await fetch('/files/Empresas.json');
          if (empresasResponse.ok) {
            const empresasText = await empresasResponse.text();
            empresasData = JSON.parse(empresasText);
            console.log("Arquivo Empresas.json carregado do servidor com sucesso");
            await saveLog('ARQUIVO_CARREGADO_SERVIDOR', { arquivo: 'Empresas.json' });
          } else {
            console.warn("Arquivo Empresas.json não encontrado no servidor, buscando nos arquivos enviados");
            await saveLog('ARQUIVO_NAO_ENCONTRADO_SERVIDOR', { arquivo: 'Empresas.json' });
          }
          
          // Tentar carregar o arquivo Veiculos.json do servidor
          const veiculosResponse = await fetch('/files/Veiculos.json');
          if (veiculosResponse.ok) {
            const veiculosText = await veiculosResponse.text();
            veiculosData = JSON.parse(veiculosText);
            console.log("Arquivo Veiculos.json carregado do servidor com sucesso");
            await saveLog('ARQUIVO_CARREGADO_SERVIDOR', { arquivo: 'Veiculos.json' });
          } else {
            console.warn("Arquivo Veiculos.json não encontrado no servidor, buscando nos arquivos enviados");
            await saveLog('ARQUIVO_NAO_ENCONTRADO_SERVIDOR', { arquivo: 'Veiculos.json' });
          }
        }
      } catch (error) {
        console.warn("Erro ao carregar arquivos do servidor:", error);
        await saveLog('AVISO_CARREGAR_ARQUIVOS_SERVIDOR', {}, null, error);
      }
      
      // Se não conseguiu carregar do servidor, fazer upload e processar os arquivos enviados
      if (!empresasData || !veiculosData) {
        // Primeiro, fazer upload dos arquivos
        const uploadedFiles = [];
        
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          setUploadProgress((prev) => ({ ...prev, [i]: 0 }));

          try {
            // Log do início do upload
            await saveLog('INICIO_UPLOAD', { fileName: file.name, fileSize: file.size });
            
            // Upload do arquivo
            const result = await uploadFile(file, (progress) => {
              setUploadProgress((prev) => ({
                ...prev,
                [i]: progress,
              }));
            });
            
            // Log do sucesso do upload
            await saveLog('UPLOAD_SUCESSO', { fileName: file.name, result });
            
            uploadedFiles.push({
              name: file.name,
              url: result.url,
              size: file.size,
              file: file // Armazenar referência ao arquivo original
            });
          } catch (error) {
            // Log do erro de upload
            await saveLog('ERRO_UPLOAD', { fileName: file.name }, null, error);
            console.error(`Erro ao fazer upload do arquivo ${file.name}:`, error);
            throw new Error(`Erro ao fazer upload do arquivo ${file.name}: ${error.message}`);
          }
        }
        
        // Processar os arquivos enviados
        for (const uploadedFile of uploadedFiles) {
          try {
            // Obter o conteúdo do arquivo
            let fileContent;
            
            // Primeiro tentar ler o arquivo diretamente
            if (uploadedFile.file) {
              const reader = new FileReader();
              
              // Criar uma Promise para aguardar a leitura do arquivo
              const readFilePromise = new Promise((resolve, reject) => {
                reader.onload = (event) => resolve(event.target.result);
                reader.onerror = (error) => reject(error);
              });
              
              // Iniciar a leitura do arquivo
              reader.readAsText(uploadedFile.file);
              
              // Aguardar a leitura do arquivo
              const fileText = await readFilePromise;
              
              // Tentar fazer o parse do JSON
              try {
                fileContent = JSON.parse(fileText);
                await saveLog('ARQUIVO_LIDO_DIRETAMENTE', { 
                  fileName: uploadedFile.name,
                  tamanho: fileText.length
                });
              } catch (parseError) {
                await saveLog('ERRO_PARSE_JSON_LOCAL', { fileName: uploadedFile.name }, null, parseError);
                throw new Error(`Erro ao analisar o JSON do arquivo ${uploadedFile.name}: ${parseError.message}`);
              }
            } 
            
            // Se não conseguiu ler diretamente, tentar pela URL
            if (!fileContent) {
              const response = await fetch(uploadedFile.url);
              if (!response.ok) {
                throw new Error(`Erro ao ler o arquivo ${uploadedFile.name}: ${response.statusText}`);
              }
              
              // Obter o texto da resposta primeiro para verificar
              const text = await response.text();
              
              // Verificar se o texto começa com <!DOCTYPE ou <html, o que indicaria HTML em vez de JSON
              if (text.trim().toLowerCase().startsWith('<!doctype') || text.trim().toLowerCase().startsWith('<html')) {
                throw new Error(`O arquivo ${uploadedFile.name} retornou HTML em vez de JSON. Verifique se o arquivo existe e está acessível.`);
              }
              
              // Tentar fazer o parse do JSON
              try {
                fileContent = JSON.parse(text);
              } catch (parseError) {
                throw new Error(`Erro ao analisar o JSON do arquivo ${uploadedFile.name}: ${parseError.message}`);
              }
            }
            
            // Verificar se o arquivo tem a estrutura esperada
            if (!fileContent.IRPFBens || !fileContent.IRPFBens[0] || !fileContent.IRPFBens[0].Tasks) {
              throw new Error(`Arquivo ${uploadedFile.name} não tem a estrutura esperada para IRPF`);
            }
            
            // Armazenar os dados conforme o nome do arquivo
            if (uploadedFile.name.toLowerCase().includes('empresa')) {
              empresasData = fileContent;
              await saveLog('DADOS_EMPRESA_CARREGADOS', { 
                fileName: uploadedFile.name,
                tamanho: JSON.stringify(fileContent).length
              });
            } else if (uploadedFile.name.toLowerCase().includes('veiculo')) {
              veiculosData = fileContent;
              await saveLog('DADOS_VEICULO_CARREGADOS', { 
                fileName: uploadedFile.name,
                tamanho: JSON.stringify(fileContent).length
              });
            } else {
              // Se o nome do arquivo não contém empresa ou veículo
              // Verificar o conteúdo para determinar o tipo
              if (fileContent.IRPFBens[0].Tasks.Empresa) {
                empresasData = fileContent;
                await saveLog('DADOS_EMPRESA_INFERIDOS', { 
                  fileName: uploadedFile.name,
                  tamanho: JSON.stringify(fileContent).length
                });
              } else if (fileContent.IRPFBens[0].Tasks.Veiculo) {
                veiculosData = fileContent;
                await saveLog('DADOS_VEICULO_INFERIDOS', { 
                  fileName: uploadedFile.name,
                  tamanho: JSON.stringify(fileContent).length
                });
              }
            }
            
          } catch (error) {
            await saveLog('ERRO_PROCESSAMENTO_ARQUIVO', { fileName: uploadedFile.name }, null, error);
            console.error(`Erro ao processar o arquivo ${uploadedFile.name}:`, error);
            throw new Error(`Erro ao processar o arquivo ${uploadedFile.name}: ${error.message}`);
          }
        }
      }
      
      // Verificar se temos os dados necessários
      if (!empresasData && !veiculosData) {
        throw new Error("Não foi possível obter os dados de Empresas e Veículos. Verifique se os arquivos foram enviados ou existem no servidor.");
      }
      
      // Estrutura para armazenar os dados processados
      const processedData = {
        IRPFBens: [
          {
            Tasks: {}
          }
        ]
      };
      
      // Processar os dados de Empresas
      if (empresasData && empresasData.IRPFBens && empresasData.IRPFBens[0] && empresasData.IRPFBens[0].Tasks) {
        const empresasTasks = empresasData.IRPFBens[0].Tasks;
        
        // Adicionar cada tipo de tarefa ao resultado processado
        Object.keys(empresasTasks).forEach(taskType => {
          if (!processedData.IRPFBens[0].Tasks[taskType]) {
            processedData.IRPFBens[0].Tasks[taskType] = [];
          }
          
          // Adicionar os itens ao array correspondente
          processedData.IRPFBens[0].Tasks[taskType] = [
            ...processedData.IRPFBens[0].Tasks[taskType],
            ...empresasTasks[taskType]
          ];
        });
      }
      
      // Processar os dados de Veículos
      if (veiculosData && veiculosData.IRPFBens && veiculosData.IRPFBens[0] && veiculosData.IRPFBens[0].Tasks) {
        const veiculosTasks = veiculosData.IRPFBens[0].Tasks;
        
        // Adicionar cada tipo de tarefa ao resultado processado
        Object.keys(veiculosTasks).forEach(taskType => {
          if (!processedData.IRPFBens[0].Tasks[taskType]) {
            processedData.IRPFBens[0].Tasks[taskType] = [];
          }
          
          // Adicionar os itens ao array correspondente
          processedData.IRPFBens[0].Tasks[taskType] = [
            ...processedData.IRPFBens[0].Tasks[taskType],
            ...veiculosTasks[taskType]
          ];
        });
      }
      
      // Adicionar rendimentos se a opção estiver ativada
      if (addFirstPhaseRevenue) {
        // Gerar valores aleatórios para os rendimentos entre 34400 e 35400
        const totalTarget = Math.random() * (35400 - 34400) + 34400; // Valor total entre 34400 e 35400
        const avgMonthly = totalTarget / 12; // Média mensal
        
        // Gerar valores para 11 meses (o último será calculado para garantir o total exato)
        const monthlyValues = [];
        let currentTotal = 0;
        
        // Gerar valores para os primeiros 11 meses
        for (let i = 0; i < 11; i++) {
          // Variação de até 5% para mais ou para menos em relação à média
          const variation = (Math.random() * 0.1 - 0.05) * avgMonthly;
          const monthValue = avgMonthly + variation;
          monthlyValues.push(parseFloat(monthValue.toFixed(2)));
          currentTotal += monthlyValues[i];
        }
        
        // Calcular o valor do último mês para atingir o total exato
        const lastMonthValue = parseFloat((totalTarget - currentTotal).toFixed(2));
        monthlyValues.push(lastMonthValue);
        
        // Criar o objeto de rendimentos
        const rendimentos = {
          janeiro: monthlyValues[0],
          fevereiro: monthlyValues[1],
          marco: monthlyValues[2],
          abril: monthlyValues[3],
          maio: monthlyValues[4],
          junho: monthlyValues[5],
          julho: monthlyValues[6],
          agosto: monthlyValues[7],
          setembro: monthlyValues[8],
          outubro: monthlyValues[9],
          novembro: monthlyValues[10],
          dezembro: monthlyValues[11]
        };
        
        // Adicionar ao resultado processado
        processedData.IRPFBens[0].Tasks.Rendimento = [rendimentos];
        
        // Log dos rendimentos gerados
        await saveLog('RENDIMENTOS_GERADOS', { 
          totalTarget, 
          rendimentos 
        });
      }
      
      // Converter valores numéricos antes de enviar ao agente
      const dataForAgent = prepareDataForAgent(processedData);
      
      // Log dos dados preparados para o agente
      await saveLog('DADOS_PREPARADOS_PARA_AGENTE', { 
        dadosOriginais: {
          amostra: JSON.stringify(processedData).substring(0, 100) + '...'
        },
        dadosConvertidos: {
          amostra: JSON.stringify(dataForAgent).substring(0, 100) + '...'
        }
      });
      
      // Enviar para o agente
      await enviarParaAgente(dataForAgent);
    } catch (error) {
      console.error('Erro ao processar múltiplos arquivos:', error);
      throw error;
    }
  };
  
  // Função para enviar dados para o agente (extraída para reutilização)
  const enviarParaAgente = async (dataForAgent) => {
    // Salvar os dados processados em um arquivo de log para referência
    await saveDetailedLog('DADOS_ENVIADOS_PARA_AGENTE', { 
      dataForAgent: JSON.parse(JSON.stringify(dataForAgent)),
      timestamp: new Date().toISOString()
    });
    
    // Log detalhado dos dados enviados para o agente
    await saveDetailedLog('ENVIANDO_PARA_AGENTE', { 
      url: 'http://host-lenovo.ddns.net:8000/IRPF/Bens',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': 'Bearer fastapi_live_f6d0d199fa86abaa4d1169983c5f3f66'
      },
      data: dataForAgent,
      timestamp: new Date().toISOString()
    });
    
    // Atualizar mensagem de processamento
    setProcessing(true);
    setError(null);
    setResult({
      message: "Enviando dados para o agente...",
      status: "sending",
      timestamp: new Date().toISOString()
    });
    
    // Salvar os dados processados em um arquivo JSON para referência
    try {
      const agentDataBlob = new Blob([JSON.stringify(dataForAgent, null, 2)], { type: 'application/json' });
      const formData = new FormData();
      formData.append('file', agentDataBlob, 'AgentIRPF.json');
      
      const saveResponse = await fetch('/api/save_log.php', {
        method: 'POST',
        body: formData
      });
      
      if (saveResponse.ok) {
        console.log('Dados do agente salvos em arquivo para referência');
        await saveLog('DADOS_AGENTE_SALVOS', { 
          arquivo: 'AgentIRPF.json',
          tamanho: JSON.stringify(dataForAgent).length
        });
      }
    } catch (saveError) {
      console.warn('Erro ao salvar dados do agente em arquivo:', saveError);
    }
    
    // Log de debug antes de enviar para o proxy
    console.log('DEBUG - Enviando dados para o proxy:', {
      url: '/api/agent_proxy.php',
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      dataSize: JSON.stringify(dataForAgent).length,
      dataPreview: JSON.stringify(dataForAgent).substring(0, 500) + '...'
    });
    
    await saveLog('ENVIANDO_PARA_PROXY', { 
      url: '/api/agent_proxy.php',
      dataSize: JSON.stringify(dataForAgent).length,
      dataPreview: JSON.stringify(dataForAgent).substring(0, 200) + '...'
    });
    
    // Usar o proxy PHP que utiliza cURL para fazer a requisição
    const agentResponse = await fetch('/api/agent_proxy.php', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(dataForAgent)
    });
    
    // Log de debug após receber resposta do proxy
    console.log('DEBUG - Resposta do proxy:', {
      status: agentResponse.status,
      statusText: agentResponse.statusText,
      headers: Object.fromEntries([...agentResponse.headers.entries()])
    });
    
    await saveLog('RESPOSTA_DO_PROXY', { 
      status: agentResponse.status,
      statusText: agentResponse.statusText,
      headers: Object.fromEntries([...agentResponse.headers.entries()])
    });
    
    // Obter o texto da resposta para log
    const responseText = await agentResponse.text();
    console.log('DEBUG - Texto da resposta:', responseText.substring(0, 500));
    
    await saveLog('TEXTO_RESPOSTA_PROXY', { 
      responsePreview: responseText.substring(0, 500)
    });
    
    let agentResult;
    
    try {
      // Tentar fazer parse do JSON da resposta
      agentResult = JSON.parse(responseText);
      console.log('DEBUG - JSON da resposta:', agentResult);
      
      await saveLog('JSON_RESPOSTA_PROXY', { 
        parsedJson: agentResult
      });
    } catch (e) {
      // Se não for um JSON válido, usar o texto como está
      agentResult = { response: responseText };
      console.error('DEBUG - Erro ao fazer parse do JSON:', e);
      
      await saveLog('ERRO_PARSE_JSON', { 
        error: e.toString(),
        responseText: responseText.substring(0, 500)
      });
    }
    
    if (!agentResponse.ok) {
      throw new Error(`Erro ao enviar dados para o agente: ${agentResponse.status} - ${responseText}`);
    }
    
    // Atualizar o estado com o resultado
    setResult({
      message: "Processamento concluído com sucesso!",
      status: "success",
      data: agentResult,
      processedData: dataForAgent, // Adicionando os dados processados para os botões de copiar/baixar
      timestamp: new Date().toISOString()
    });
    
    // Log do resultado final
    await saveLog('PROCESSAMENTO_CONCLUIDO', { 
      status: "success",
      timestamp: new Date().toISOString()
    });
  };

  return (
    <div className="max-w-6xl mx-auto">
      {/* Cabeçalho */}
      <div className="flex items-center gap-4 mb-8">
        <Button
          variant="outline"
          size="icon"
          onClick={() => window.history.back()}
        >
          <ArrowLeft className="w-4 h-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Agent IRPF</h1>
          <p className="text-gray-500">
            Assistente para automação de tarefas relacionadas ao IRPF
          </p>
        </div>
      </div>

      {/* Layout principal */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Coluna da esquerda - Área principal */}
        <div className="lg:col-span-2 space-y-6">
          {/* Opção de adicionar rendimentos da 1ª fase */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Configurações</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="bg-gradient-to-r from-teal-50 to-cyan-50 p-6 rounded-lg border border-teal-100 shadow-sm">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <div className="w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <DollarSign className="w-5 h-5 text-teal-600" />
                      </div>
                      <Label htmlFor="add-first-phase" className="text-lg font-medium text-gray-800">
                        Rendimento da 1° Fase
                      </Label>
                    </div>
                    <p className="text-sm text-gray-600 ml-12">
                      Ative esta opção para incluir automaticamente rendimentos da primeira fase no processamento
                    </p>
                  </div>
                  
                  <div className="flex items-center justify-end mt-4 md:mt-0">
                    <div 
                      className={`relative inline-flex h-9 w-16 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                        addFirstPhaseRevenue ? 'bg-teal-500' : 'bg-gray-200'
                      }`}
                      onClick={() => setAddFirstPhaseRevenue(!addFirstPhaseRevenue)}
                    >
                      <span className="sr-only">Adicionar rendimento da 1ª fase</span>
                      <span
                        aria-hidden="true"
                        className={`pointer-events-none inline-block h-8 w-8 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          addFirstPhaseRevenue ? 'translate-x-7' : 'translate-x-0'
                        }`}
                      />
                    </div>
                    <span className="ml-3 text-sm font-medium">
                      {addFirstPhaseRevenue ? (
                        <span className="text-teal-600 font-semibold">Ativado</span>
                      ) : (
                        <span className="text-gray-500">Desativado</span>
                      )}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Área de upload */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Upload de Arquivos JSON</CardTitle>
            </CardHeader>
            <CardContent>
              <div
                className={`border-2 border-dashed rounded-xl p-8 transition-colors ${
                  dragActive
                    ? "border-sky-200 bg-sky-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="text-center">
                  <div className="w-16 h-16 bg-sky-50 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Upload className="w-8 h-8 text-sky-600" />
                  </div>
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Arraste seus arquivos JSON aqui
                  </h3>
                  <p className="text-gray-500 mb-4">
                    ou clique para selecionar arquivos do seu computador
                  </p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".json"
                    multiple
                    onChange={handleFileInput}
                    className="hidden"
                  />
                  <Button
                    onClick={handleButtonClick}
                    className="bg-sky-600 hover:bg-sky-700 text-white"
                  >
                    Selecionar Arquivos
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Arquivos selecionados */}
          <AnimatePresence>
            {files.length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg">Arquivos Selecionados</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className="flex items-center justify-between p-4 bg-white rounded-lg border"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gray-50 rounded-lg flex items-center justify-center">
                              <FileJson className="w-5 h-5 text-gray-400" />
                            </div>
                            <div>
                              <p className="font-medium">{file.name}</p>
                              <p className="text-sm text-gray-500">
                                {formatFileSize(file.size)}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            {uploading ? (
                              <div className="w-32">
                                <Progress value={uploadProgress[index] || 0} />
                              </div>
                            ) : (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => removeFile(index)}
                              >
                                <X className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="mt-4">
                      <Button 
                        onClick={startAgent} 
                        disabled={files.length === 0 || processing}
                        className="flex items-center gap-2 w-full bg-green-600 hover:bg-green-700 text-white border-2 border-green-500 shadow-md"
                      >
                        {processing ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Processando...
                          </>
                        ) : (
                          <>
                            <Bot className="h-4 w-4" />
                            Start Agent
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Resultado do processamento */}
          <AnimatePresence>
            {result && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <Card>
                  <CardHeader className="flex flex-row items-center justify-between">
                    <CardTitle className="text-lg">Resultado do Processamento</CardTitle>
                  </CardHeader>
                  <CardContent>
                    {result.status === "success" ? (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start">
                        <CheckCircle2 className="w-5 h-5 text-green-500 mt-0.5 mr-3 flex-shrink-0" />
                        <div>
                          <h3 className="font-medium text-green-800">{result.message}</h3>
                          <p className="text-green-700 text-sm">
                            Processamento concluído em: {new Date(result.timestamp).toLocaleString()}
                          </p>
                          {result.agentResponse && (
                            <div className="mt-4">
                              <h4 className="font-medium text-gray-800 mb-2">Resposta do Agente:</h4>
                              <pre className="bg-gray-50 p-3 rounded-md overflow-auto max-h-60 text-xs">
                                {JSON.stringify(result.agentResponse, null, 2)}
                              </pre>
                            </div>
                          )}
                          <div className="mt-4 flex space-x-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (result.processedData) {
                                  navigator.clipboard.writeText(JSON.stringify(result.processedData, null, 2))
                                    .then(() => {
                                      alert("JSON copiado para a área de transferência!");
                                    })
                                    .catch(err => {
                                      console.error("Erro ao copiar para a área de transferência:", err);
                                      alert("Erro ao copiar para a área de transferência");
                                    });
                                }
                              }}
                            >
                              <Copy className="w-4 h-4 mr-2" />
                              Copiar JSON
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                if (result.processedData) {
                                  const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(result.processedData, null, 2));
                                  const downloadAnchorNode = document.createElement('a');
                                  downloadAnchorNode.setAttribute("href", dataStr);
                                  downloadAnchorNode.setAttribute("download", "AgentIRPF.json");
                                  document.body.appendChild(downloadAnchorNode);
                                  downloadAnchorNode.click();
                                  downloadAnchorNode.remove();
                                }
                              }}
                            >
                              <Download className="w-4 h-4 mr-2" />
                              Baixar JSON
                            </Button>
                          </div>
                        </div>
                      </div>
                    ) : result.status === "sending" ? (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start">
                        <Loader2 className="w-5 h-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0 animate-spin" />
                        <div>
                          <h3 className="font-medium text-blue-800">{result.message}</h3>
                          <p className="text-blue-700 text-sm">
                            Iniciado em: {new Date(result.timestamp).toLocaleString()}
                          </p>
                          <p className="text-blue-700 text-sm mt-2">
                            Aguarde enquanto os dados são enviados e processados pelo agente...
                          </p>
                        </div>
                      </div>
                    ) : (
                      <pre className="bg-gray-50 p-4 rounded-lg overflow-auto max-h-96 text-sm">
                        {JSON.stringify(result, null, 2)}
                      </pre>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Mensagem de erro */}
          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
              >
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start">
                  <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <h3 className="font-medium text-red-800">Erro</h3>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Coluna da direita - Informações */}
        <div className="space-y-6">
          {/* Card de informações */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sobre o Agent IRPF</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-teal-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <Bot className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Assistente Inteligente</h3>
                    <p className="text-sm text-gray-500">
                      O Agent IRPF automatiza tarefas relacionadas à declaração do Imposto de Renda Pessoa Física.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-indigo-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <FileJson className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Processamento de JSON</h3>
                    <p className="text-sm text-gray-500">
                      Faça upload de arquivos JSON para estruturar informações para a declaração do IRPF.
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 bg-amber-50 rounded-full flex items-center justify-center flex-shrink-0">
                    <CheckCircle2 className="w-5 h-5 text-amber-600" />
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-900">Rendimentos da 1ª Fase</h3>
                    <p className="text-sm text-gray-500">
                      Opção para incluir automaticamente rendimentos da primeira fase na sua declaração.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-6 pt-6 border-t border-gray-100">
                <h3 className="font-medium text-gray-900 mb-2">Como usar</h3>
                <ol className="text-sm text-gray-500 space-y-2 list-decimal list-inside">
                  <li>Selecione se deseja adicionar rendimentos da 1ª fase</li>
                  <li>Faça upload dos arquivos JSON com os dados para processamento</li>
                  <li>Clique em "Start Agent" para iniciar o processamento</li>
                  <li>Aguarde enquanto os dados são enviados para o agente</li>
                  <li>Visualize a resposta do agente e, se necessário, baixe o JSON processado</li>
                </ol>
              </div>
            </CardContent>
          </Card>

          {/* Card de status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Status do Processamento</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Arquivos selecionados:</span>
                  <span className="text-sm">{files.length}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Incluir rendimentos 1ª fase:</span>
                  <span className="text-sm">
                    {addFirstPhaseRevenue ? (
                      <span className="text-teal-600 font-semibold">Ativado</span>
                    ) : (
                      <span className="text-gray-500">Desativado</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Status:</span>
                  <span className="text-sm">
                    {processing ? (
                      <span className="text-amber-600">Processando...</span>
                    ) : result && result.status === "success" ? (
                      <span className="text-green-600">Concluído</span>
                    ) : result && result.status === "sending" ? (
                      <span className="text-blue-600">Enviando...</span>
                    ) : error ? (
                      <span className="text-red-600">Erro</span>
                    ) : (
                      <span className="text-gray-500">Aguardando</span>
                    )}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium">Endpoint do Agente:</span>
                  <span className="text-sm text-gray-500 truncate max-w-[180px]">host-lenovo.ddns.net:8000</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
